import React from 'react';

import Dropdown, { DropdownOption } from '../Dropdown';
import ContentPicker, { ContentPickerItem } from '../ContentPicker';
import FormActions from '../FormActions';

import {
  Container,
  FiltersRow,
  PickerColumn,
  FormColumn,
  SectionTitle,
  FieldsGrid,
  FullWidth,
} from './styles';

interface ContentEditorLayoutProps {
  /** Módulos do curso selecionado. */
  moduleOptions: DropdownOption[];
  moduleId: string;
  onModuleChange: (id: string) => void;
  modulesLoading?: boolean;

  /** Aulas do módulo (opcional — a aba Módulo não usa). */
  lessonOptions?: DropdownOption[];
  lessonId?: string;
  onLessonChange?: (id: string) => void;
  lessonsLoading?: boolean;

  /** Itens já cadastrados, para pesquisar e popular o formulário. */
  pickerItems: ContentPickerItem[];
  pickerTitle: string;
  selectedItemId: string;
  onSelectItem: (id: string) => void;
  pickerLoading?: boolean;

  /** Campos do formulário. */
  children: React.ReactNode;

  /** Ações. */
  editing: boolean;
  dirty: boolean;
  saving?: boolean;
  deleting?: boolean;
  canDelete?: boolean;
  onSave: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onClear: () => void;
}

/**
 * Casca comum de todos os editores de conteúdo: filtros no topo (módulo e
 * aula), lista pesquisável à esquerda, formulário à direita e a barra de ações
 * fixa embaixo. A tela não muda de posição — só o conteúdo dos campos.
 */
const ContentEditorLayout: React.FC<ContentEditorLayoutProps> = ({
  moduleOptions,
  moduleId,
  onModuleChange,
  modulesLoading,
  lessonOptions,
  lessonId,
  onLessonChange,
  lessonsLoading,
  pickerItems,
  pickerTitle,
  selectedItemId,
  onSelectItem,
  pickerLoading,
  children,
  editing,
  dirty,
  saving,
  deleting,
  canDelete,
  onSave,
  onEdit,
  onDelete,
  onClear,
}) => {
  return (
    <Container>
      <FiltersRow>
        <Dropdown
          label="Módulo"
          placeholder="Selecione o módulo"
          options={moduleOptions}
          value={moduleId}
          onChange={onModuleChange}
          disabled={modulesLoading}
          emptyMessage={modulesLoading ? 'Carregando módulos…' : 'Nenhum módulo neste curso'}
        />

        {lessonOptions && onLessonChange && (
          <Dropdown
            label="Aula"
            placeholder="Selecione a aula"
            options={lessonOptions}
            value={lessonId ?? ''}
            onChange={onLessonChange}
            disabled={lessonsLoading || !moduleId}
            emptyMessage={lessonsLoading ? 'Carregando aulas…' : 'Nenhuma aula neste módulo'}
          />
        )}
      </FiltersRow>

      <PickerColumn>
        <ContentPicker
          items={pickerItems}
          selectedId={selectedItemId}
          onSelect={onSelectItem}
          loading={pickerLoading}
          title={pickerTitle}
        />
      </PickerColumn>

      <FormColumn>
        <SectionTitle>{editing ? 'Editando item' : 'Novo item'}</SectionTitle>

        <FieldsGrid>{children}</FieldsGrid>

        <FullWidth>
          <FormActions
            editing={editing}
            dirty={dirty}
            saving={saving}
            deleting={deleting}
            canDelete={canDelete}
            onSave={onSave}
            onEdit={onEdit}
            onDelete={onDelete}
            onClear={onClear}
          />
        </FullWidth>
      </FormColumn>
    </Container>
  );
};

export default ContentEditorLayout;
